export const DEFAULT_SQL_REFS = { cell: 'cv', item: 'i' };
/** monday's blank sentinel inside filter compare values (`numbers`, `timeline`) `[09§A.7]`. */
export const BLANK_COMPARE_VALUE = '$$$blank$$$';
export function sqlFragment(text, params = []) {
    return { text, params };
}
function refsOf(ctx) {
    return ctx.refs ?? DEFAULT_SQL_REFS;
}
/** True when the compare list is only monday's blank sentinel (translate to `is_empty`). */
export function isBlankOnly(compareValues) {
    return compareValues.length > 0 && compareValues.every((value) => value === BLANK_COMPARE_VALUE);
}
function shadowColumn(refs, shadow) {
    return `${refs.cell}.${shadow}_value`;
}
/**
 * How much of `cell_values.text_value` the `cell_values_text_idx` btree holds.
 *
 * **Why the index is a prefix at all.** A btree index tuple cannot exceed 2,704 bytes, and a
 * real `long_text` cell on this account renders to 2,936 (board `5325206328`), which aborted
 * the whole transaction with SQLSTATE `54000` — for the *importer* and for `upsertCellValue`,
 * i.e. for a user pasting a long note into a cell (T-X2 round 5p, a production defect).
 * Migration 0017 therefore indexes `(board_id, column_id, left(text_value, N))`.
 *
 * **Why 512.** `left()` counts *characters* and the limit is *bytes*: 512 characters is at most
 * 2,048 bytes of UTF-8, leaving room for `board_id` (8) and a `column_id` of up to 255 bytes
 * plus tuple overhead, so the index row can never overflow whatever a user types.
 *
 * The equality predicates below use the same expression so the index is still chosen: for a
 * compare value shorter than the prefix, `left(text_value, 512) = X` and `text_value = X` are
 * the *same* predicate, and for a longer one the exact comparison is AND-ed back on.
 */
export const TEXT_SHADOW_PREFIX_CHARS = 512;
/** `left(<cell>.text_value, 512)` — the expression `cell_values_text_idx` is built on. */
export function textShadowPrefix(cell) {
    return `left(${cell}.text_value, ${String(TEXT_SHADOW_PREFIX_CHARS)})`;
}
/**
 * Text equality against the shadow, written so the prefix index can serve it. Values longer
 * than the prefix keep an exact recheck, so the result set is identical either way.
 */
function textAnyOf(refs, values, negated, override) {
    const column = override ?? shadowColumn(refs, 'text');
    // An overridden expression is not what `cell_values_text_idx` is built on, so the prefix
    // rewrite buys nothing there and would only make the predicate wrong-looking.
    const prefixed = override ?? textShadowPrefix(refs.cell);
    /*
     * `left(t, N) = X` is the same predicate as `t = X` only while `X` is **shorter** than the
     * prefix: at exactly N characters a longer `t` with that prefix would match too, so the
     * exact comparison is AND-ed back on from N upwards, not from N + 1.
     */
    const overlong = values.some((value) => typeof value === 'string' && value.length >= TEXT_SHADOW_PREFIX_CHARS);
    const positive = overlong
        ? `(${prefixed} = ANY(?::text[]) AND ${column} = ANY(?::text[]))`
        : `${prefixed} = ANY(?::text[])`;
    const params = overlong
        ? [values.map((value) => (typeof value === 'string' ? value.slice(0, TEXT_SHADOW_PREFIX_CHARS) : value)), values]
        : [values];
    return negated
        ? sqlFragment(`(${column} IS NULL OR NOT ${positive})`, params)
        : sqlFragment(positive, params);
}
const CAST = { text: 'text[]', number: 'numeric[]', date: 'date[]' };
/**
 * The default operator table of M1-B5 §5.3. Returns `null` when the operator is not offered for
 * that shadow — the caller must have validated against `filterOperators` first.
 *
 * An empty cell is the **absence** of a `cell_values` row, so emptiness is always tested on
 * `cv.item_id`, never on the shadow being null (M1-B5 §4).
 */
export function defaultFilterSql(shadow, ctx, 
/**
 * The expression the operators compare, when it is **not** the plain shadow column: the
 * `end` half of a `timeline` is `(cv.value->>'to')::timestamptz`, not `cv.date_value`
 * (`compare_attribute`, `specs/M1-B6-filters-sort.md` §3.3). Emptiness is still the absence
 * of the `cell_values` row, so it keeps reading `cv.item_id`.
 */
override) {
    const refs = refsOf(ctx);
    const column = override ?? shadowColumn(refs, shadow);
    const values = ctx.compareValues;
    const first = values[0];
    if (shadow !== 'text' && isBlankOnly(values)) {
        return sqlFragment(`${refs.cell}.item_id IS NULL`);
    }
    switch (ctx.operator) {
        case 'is_empty':
            return sqlFragment(`${refs.cell}.item_id IS NULL`);
        case 'is_not_empty':
            return sqlFragment(`${refs.cell}.item_id IS NOT NULL`);
        case 'any_of':
            if (shadow === 'date')
                return sqlFragment(`${column}::date = ANY(?::${CAST.date})`, [values]);
            if (shadow === 'text')
                return textAnyOf(refs, values, false, override);
            return sqlFragment(`${column} = ANY(?::${CAST[shadow]})`, [values]);
        case 'not_any_of':
            if (shadow === 'date') {
                return sqlFragment(`(${column} IS NULL OR NOT (${column}::date = ANY(?::${CAST.date})))`, [values]);
            }
            if (shadow === 'text')
                return textAnyOf(refs, values, true, override);
            return sqlFragment(`(${column} IS NULL OR NOT (${column} = ANY(?::${CAST[shadow]})))`, [values]);
        case 'contains_text':
            return shadow === 'text' ? sqlFragment(`${column} ILIKE '%' || ? || '%'`, [first]) : null;
        case 'not_contains_text':
            return shadow === 'text'
                ? sqlFragment(`(${column} IS NULL OR ${column} NOT ILIKE '%' || ? || '%')`, [first])
                : null;
        case 'starts_with':
            return shadow === 'text' ? sqlFragment(`${column} ILIKE ? || '%'`, [first]) : null;
        case 'ends_with':
            return shadow === 'text' ? sqlFragment(`${column} ILIKE '%' || ?`, [first]) : null;
        case 'contains_terms':
            return shadow === 'text'
                ? sqlFragment(`to_tsvector('simple', ${column}) @@ plainto_tsquery('simple', ?)`, [first])
                : null;
        case 'greater_than':
            return shadow === 'text' ? null : sqlFragment(`${column} > ?`, [first]);
        case 'greater_than_or_equals':
            return shadow === 'text' ? null : sqlFragment(`${column} >= ?`, [first]);
        case 'lower_than':
            return shadow === 'text' ? null : sqlFragment(`${column} < ?`, [first]);
        case 'lower_than_or_equal':
            return shadow === 'text' ? null : sqlFragment(`${column} <= ?`, [first]);
        case 'between':
            return shadow === 'text' ? null : sqlFragment(`${column} BETWEEN ? AND ?`, [first, values[1]]);
        case 'within_the_last':
            return shadow === 'date'
                ? sqlFragment(`${column} BETWEEN now() - (? * interval '1 day') AND now()`, [first])
                : null;
        case 'within_the_next':
            return shadow === 'date'
                ? sqlFragment(`${column} BETWEEN now() AND now() + (? * interval '1 day')`, [first])
                : null;
        default:
            return null;
    }
}
/** The default aggregation table of M1-B5 §5.3. `null` when the type does not offer it. */
export function defaultSummarySql(ctx) {
    const refs = refsOf(ctx);
    const number = shadowColumn(refs, 'number');
    const date = shadowColumn(refs, 'date');
    const scalar = (text) => ({ text, params: [], shape: 'scalar' });
    switch (ctx.aggregation) {
        case 'none':
            return null;
        case 'sum':
            return scalar(`sum(${number})`);
        case 'avg':
            return scalar(`avg(${number})`);
        case 'min':
            return scalar(`min(${number})`);
        case 'max':
            return scalar(`max(${number})`);
        case 'median':
            return scalar(`percentile_cont(0.5) WITHIN GROUP (ORDER BY ${number})`);
        case 'count':
            return scalar(`count(${refs.item}.id)`);
        case 'count_filled':
            return scalar(`count(${refs.cell}.item_id)`);
        case 'count_empty':
            return scalar(`count(${refs.item}.id) - count(${refs.cell}.item_id)`);
        case 'earliest':
            return scalar(`min(${date})`);
        case 'latest':
            return scalar(`max(${date})`);
        case 'range':
            return { text: `min(${date}), max(${date})`, params: [], shape: 'pair' };
        default:
            return null;
    }
}
//# sourceMappingURL=sql.js.map